/**
 * /tools/letter-to-future-self?clientId=X — NL-044 (Step 9).
 *
 * The client writes a single letter to their future self. Persists as a
 * versioned `client_documents` row with `doc_type='future_self_letter'`.
 * NOT sealed — explicitly distinct from the legacy-letter sealed flow.
 *
 * The form has four fields: optional salutation + required body +
 * optional sign-off + optional "intended open date". The macOS coach
 * reads the latest row when assembling the graduation envelope.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  salutation: string;
  body: string;
  signoff: string;
  intendedOpenDate: string;
}

/**
 * IMP-018 / NL-044 AC — the open date pre-fills to one year from today
 * (local timezone), still freely editable. Feb 29 rolls over to Mar 1 via
 * native Date semantics, which is fine for a "roughly a year out" default.
 */
function defaultOpenDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

const DEFAULT_FORM: FormState = {
  salutation: '',
  body: '',
  signoff: '',
  intendedOpenDate: '',
};

export function LetterToFutureSelfPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(() => ({
    ...DEFAULT_FORM,
    intendedOpenDate: defaultOpenDate(),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.letterToFutureSelf.errors.missingClient'));
      return;
    }
    const body = form.body.trim();
    if (body.length === 0) {
      setSubmitError(t('tools.letterToFutureSelf.errors.bodyRequired'));
      return;
    }
    setSubmitting(true);
    const payload: Record<string, unknown> = { body };
    if (form.salutation.trim()) payload.salutation = form.salutation.trim();
    if (form.signoff.trim()) payload.signoff = form.signoff.trim();
    if (form.intendedOpenDate.trim()) payload.intendedOpenDate = form.intendedOpenDate.trim();
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'letter-to-future-self',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.letterToFutureSelf.errors.generic'));
      } else {
        setSubmitError(t('tools.letterToFutureSelf.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="letter-to-future-self-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.letterToFutureSelf.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.letterToFutureSelf.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.letterToFutureSelf.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.letterToFutureSelf.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.letterToFutureSelf.description')}
        </p>
        <p className="mt-s2 text-xs italic text-gray-1">
          {t('tools.letterToFutureSelf.notSealedHint')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.letterToFutureSelf.fields.salutation')}</span>
            <input
              type="text"
              value={form.salutation}
              onChange={(e) => setForm((prev) => ({ ...prev, salutation: e.target.value }))}
              placeholder={t('tools.letterToFutureSelf.placeholders.salutation')}
              maxLength={200}
              className="input-field mt-s2 w-full"
              data-testid="letter-to-future-self-salutation"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.letterToFutureSelf.fields.body')}</span>
            <textarea
              value={form.body}
              onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
              placeholder={t('tools.letterToFutureSelf.placeholders.body')}
              rows={14}
              maxLength={10000}
              className="input-field mt-s2 w-full font-display"
              data-testid="letter-to-future-self-body"
              required
            />
            <p className="mt-s1 text-xs text-gray-1">
              {t('tools.letterToFutureSelf.hints.body')}
            </p>
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.letterToFutureSelf.fields.signoff')}</span>
            <input
              type="text"
              value={form.signoff}
              onChange={(e) => setForm((prev) => ({ ...prev, signoff: e.target.value }))}
              placeholder={t('tools.letterToFutureSelf.placeholders.signoff')}
              maxLength={200}
              className="input-field mt-s2 w-full"
              data-testid="letter-to-future-self-signoff"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.letterToFutureSelf.fields.intendedOpenDate')}
            </span>
            <input
              type="date"
              value={form.intendedOpenDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, intendedOpenDate: e.target.value }))
              }
              className="input-field mt-s2"
              data-testid="letter-to-future-self-open-date"
            />
            <p className="mt-s1 text-xs text-gray-1">
              {t('tools.letterToFutureSelf.hints.intendedOpenDate')}
            </p>
          </label>
        </article>

        <FinalizeWarning />

        {submitError && (
          <p className="form-error" role="alert" data-testid="letter-to-future-self-error">
            {submitError}
          </p>
        )}
        {savedAt && (
          <p
            className="text-flame-deep text-sm"
            role="status"
            data-testid="letter-to-future-self-saved"
          >
            {t('tools.letterToFutureSelf.saved')}
          </p>
        )}

        <div className="flex justify-end gap-s3">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
            data-testid="letter-to-future-self-submit"
          >
            {submitting
              ? t('tools.letterToFutureSelf.submitting')
              : t('tools.letterToFutureSelf.submit')}
          </button>
        </div>
      </form>
    </section>
  );
}
